/**
 * 内置元素插件注册（唯一副作用入口）。
 *
 * 新增元素类型流程：
 * 1. types/ — 元素数据类型
 * 2. store 或 document — 默认创建逻辑（如 addXxxElement）
 * 3. components/elements/XxxElement.tsx — 画布 React 组件
 * 4. components/interaction/selection/handles/ — 可选专属把手
 * 5. components/property-panels/ — 属性面板
 * 6. core/export/drawElement.ts — 导出绘制分支
 * 7. 在本文件末尾调用 registerElementPlugin({ ... })
 *
 * 勿在 components/elements/index.ts 顶层注册，避免 import 组件即污染全局 registry。
 */
import type { ComponentType } from 'react';
import { registerElementPlugin } from '@/components/canvas/elements/registry/elementRegistry';
import { TextElement } from '@/components/canvas/elements/text/TextElement';
import { ImageElement } from '@/components/canvas/elements/image/ImageElement';
import {
  LineElement,
  RectangleElement,
  CircleElement,
  TriangleElement,
  VectorElement,
  FreehandElement,
} from '@/components/canvas/elements/shape';
import { GroupElement } from '@/components/canvas/elements/group/GroupElement';
import { TableElement } from '@/components/canvas/elements/table/TableElement';
import { ChartElement } from '@/components/canvas/elements/chart/ChartElement';
import { LineHandles } from '@/components/canvas/selection/handles/LineHandles';

let registered = false;

export function registerBuiltinElements(): void {
  if (registered) return;
  registered = true;

  registerElementPlugin({ type: 'text', displayName: '文本', Component: TextElement as ComponentType<any> });
  registerElementPlugin({ type: 'image', displayName: '图片', Component: ImageElement as ComponentType<any> });
  registerElementPlugin({
    type: 'line',
    displayName: '直线',
    Component: LineElement as ComponentType<any>,
    HandlesComponent: LineHandles as ComponentType<any>,
  });
  registerElementPlugin({ type: 'rect', displayName: '矩形', Component: RectangleElement as ComponentType<any> });
  registerElementPlugin({ type: 'circle', displayName: '圆形', Component: CircleElement as ComponentType<any> });
  registerElementPlugin({ type: 'triangle', displayName: '三角形', Component: TriangleElement as ComponentType<any> });
  registerElementPlugin({ type: 'vector', displayName: '矢量图形', Component: VectorElement as ComponentType<any> });
  registerElementPlugin({ type: 'freehand', displayName: '涂抹笔', Component: FreehandElement as ComponentType<any> });
  registerElementPlugin({ type: 'table', displayName: '表格', Component: TableElement as ComponentType<any> });
  registerElementPlugin({ type: 'chart', displayName: '图表', Component: ChartElement as ComponentType<any> });
  registerElementPlugin({ type: 'group', displayName: '组', Component: GroupElement as ComponentType<any> });
}

/** 在渲染画布/缩略图/把手前调用，幂等。 */
export function ensureBuiltinElementsRegistered(): void {
  registerBuiltinElements();
}
